// Client-side validation for the /admin template editor. Mirrors the
// StoryTemplateDoc sub-shapes in @spirits/shared. Template writes go straight to
// Firestore (firestore.rules gates by admin email), so this zod pass is the real
// guard against malformed docs — run it before every save.

import { z } from 'zod';
import { STORY_STAGES, type SceneCategory, type StoryTemplateDoc } from '@spirits/shared';

export const CATEGORIES: SceneCategory[] = ['fiction', 'science', 'history', 'crime'];
const BOT_ACTION_STATES = ['idle', 'cooking', 'sleeping', 'pacing', 'reading', 'working'] as const;

const nonEmpty = z.string().trim().min(1, 'required');

const arcSchema = z.object({
  id: nonEmpty,
  title: nonEmpty,
  description: nonEmpty,
});

const characterSchema = z.object({
  key: nonEmpty,
  displayName: nonEmpty,
  persona: nonEmpty,
  aspiration: nonEmpty,
  arcs: z.array(arcSchema).min(1, 'at least one arc').max(4, 'at most 4 arcs'),
  arcLockScene: z.number().int().nonnegative(),
});

const actionOptionSchema = z.object({
  id: nonEmpty,
  label: nonEmpty,
  consequence: nonEmpty,
  arcId: nonEmpty,
});

const decisionSchema = z.object({
  situation: nonEmpty,
  wish: nonEmpty,
  fear: nonEmpty,
  callToAction: z.string().optional(),
  actions: z.array(actionOptionSchema).min(1, 'at least one road'),
});

const sceneBotSchema = z.object({
  key: nonEmpty,
  actionState: z.enum(BOT_ACTION_STATES),
  order: z.number().int().nonnegative().optional(),
  decision: decisionSchema,
});

const sceneSchema = z.object({
  name: nonEmpty,
  stage: z.enum(STORY_STAGES as [string, ...string[]]),
  blurb: z.string().optional(),
  description: z.string().optional(),
  instruction: z.string().optional(),
  bots: z.array(sceneBotSchema).optional(),
});

const endingSchema = z.object({
  id: nonEmpty,
  title: nonEmpty,
  summary: nonEmpty,
  isCanon: z.boolean().optional(),
  requiredArcs: z.record(z.string(), z.string()).optional(),
});

const canonSchema = z.object({
  source: nonEmpty,
  summary: nonEmpty,
});

export const castSchema = z.array(characterSchema).min(1, 'at least one character');
export const scenesSchema = z.array(sceneSchema).min(1, 'at least one scene (the opening)');
export const endingsSchema = z.array(endingSchema).min(1, 'at least one ending');
export { canonSchema };

// A single "block" of the doc, edited as raw JSON in the editor.
export type Block = 'cast' | 'scenes' | 'endings' | 'canon';

export type ParseResult<T> = { ok: true; value: T } | { ok: false; error: string };

function formatIssues(error: z.ZodError): string {
  return error.issues
    .map((i) => {
      const path = i.path.length ? i.path.join('.') : '(root)';
      return `• ${path}: ${i.message}`;
    })
    .join('\n');
}

// Parse a JSON string and validate it against a schema. Returns a flat error
// string (JSON syntax error or a bullet list of schema issues) on failure.
export function parseBlock<T>(raw: string, schema: z.ZodType<T>): ParseResult<T> {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch (e) {
    return { ok: false, error: `Invalid JSON: ${(e as Error).message}` };
  }
  const result = schema.safeParse(parsed);
  if (!result.success) return { ok: false, error: formatIssues(result.error) };
  return { ok: true, value: result.data };
}

export type TemplateDocFields = Omit<StoryTemplateDoc, 'schemaVersion' | 'seedKey' | 'createdAt'>;

// Pretty-print a value for a JSON block (2-space indent).
export function toJson(value: unknown): string {
  return JSON.stringify(value ?? null, null, 2);
}

// A fresh template scaffold — minimal but valid in shape, ready to fill in.
export function blankTemplate(): {
  id: string;
  title: string;
  category: SceneCategory;
  blurb: string;
  canonText: string;
  castText: string;
  scenesText: string;
  endingsText: string;
} {
  const cast = [
    {
      key: 'hero',
      displayName: 'Hero',
      persona: 'You are ...',
      aspiration: 'To ...',
      arcLockScene: 3,
      arcs: [
        { id: 'arc-a', title: 'Arc A', description: 'Who they become down road A.' },
        { id: 'arc-b', title: 'Arc B', description: 'Who they become down road B.' },
      ],
    },
  ];
  const scenes = [
    {
      name: 'Opening',
      stage: 'opening',
      blurb: 'One-line setup for the opening scene.',
      description: 'The atmosphere and setting of the opening, fed to the bots.',
      bots: [
        {
          key: 'hero',
          actionState: 'idle',
          order: 0,
          decision: {
            situation: 'What the character is facing, in their own voice.',
            wish: 'Their quiet wish.',
            fear: 'Their quiet fear.',
            actions: [
              {
                id: 'road-a',
                label: 'Take road A',
                consequence: 'What road A does, as authored narration.',
                arcId: 'arc-a',
              },
              {
                id: 'road-b',
                label: 'Take road B',
                consequence: 'What road B does, as authored narration.',
                arcId: 'arc-b',
              },
            ],
          },
        },
      ],
    },
    {
      name: 'Rising Action',
      stage: 'conflict',
      instruction: 'Steer the generator: what this skeleton scene should do.',
    },
  ];
  const endings = [
    {
      id: 'ending-a',
      title: 'Ending A',
      summary: 'What this ending is and its tone.',
      isCanon: true,
      requiredArcs: { hero: 'arc-a' },
    },
    {
      id: 'ending-b',
      title: 'Ending B',
      summary: 'What this ending is and its tone.',
      requiredArcs: { hero: 'arc-b' },
    },
  ];
  return {
    id: '',
    title: 'Untitled Story',
    category: 'fiction',
    blurb: '',
    canonText: '',
    castText: toJson(cast),
    scenesText: toJson(scenes),
    endingsText: toJson(endings),
  };
}
